import { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logout } from "../../authSlice";
import "../../styling/Header.css";
import { IoNotifications } from "react-icons/io5";
import { useProfile } from "../../ProfileContext";
import { EventDataContext } from "../EventDataContext";
import { FaUserCircle } from "react-icons/fa";
import { MdDashboard, MdOutlineLogout } from "react-icons/md";
import { FaRegUser } from "react-icons/fa6";
// --- NEW ICONS ---
import { PiDotsThreeOutlineFill } from "react-icons/pi";
import { FaTrash } from "react-icons/fa";
import defaultProfilePic from "../../images/defaultPic.svg";


const ClientHeader = ({ clientId, onAccountDropdownClick, setInitialBusinessEventTab, handleTabSwitch, setShowAccountInfo, businessId }) => {
  const { EventData, setEventData } = useContext(EventDataContext);
  const { profileImage, setProfileImage } = useProfile();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const notificationIconRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const profileIconRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const deleteMenuRef = useRef(null);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [logo, setLogo] = useState(null);
  
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  
  // --- UPDATED STATE ---
  const [openDeleteMenu, setOpenDeleteMenu] = useState(null); // Stores the *index* of the open menu
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 }); // Stores click coordinates

  const defaultLogo = "https://cdn.prod.website-files.com/65997e123627c8e86f726abe/65a5db5cbf3e4af51b463bd7_Boothbloom-logo-color.svg";

  // --- NEW: Click Outside detector ---
  useEffect(() => {
    // If nothing is open, don't add the listener
    if (!showNotifications && !showProfileDropdown && openDeleteMenu === null) {
      return;
    }

    const handleClickOutside = (event) => {
      // Check for Profile dropdown
      const isClickInsideProfileIcon = profileIconRef.current && profileIconRef.current.contains(event.target);
      const isClickInsideProfileDropdown = profileDropdownRef.current && profileDropdownRef.current.contains(event.target);

      if (showProfileDropdown && !isClickInsideProfileIcon && !isClickInsideProfileDropdown) {
        setShowProfileDropdown(false);
      }

      // Check for Notification dropdown AND Delete menu
      const isClickInsideNotifIcon = notificationIconRef.current && notificationIconRef.current.contains(event.target);
      const isClickInsideNotifDropdown = notificationDropdownRef.current && notificationDropdownRef.current.contains(event.target);
      const isClickInsideDeleteMenu = deleteMenuRef.current && deleteMenuRef.current.contains(event.target);

      // The 3-dots icon is *inside* notificationDropdownRef, so its click is safe
      // and will be handled by its own onClick.

      if ((showNotifications || openDeleteMenu !== null) && !isClickInsideNotifIcon && !isClickInsideNotifDropdown && !isClickInsideDeleteMenu) {
        setShowNotifications(false);
        setOpenDeleteMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications, showProfileDropdown, openDeleteMenu]);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/businessLogo/${businessId}`,
          { withCredentials: true }
        );
        setLogo(res.data.data.brandLogoUrl);
      } catch (err)
      {
        console.error("Error fetching brand logo:", err);
      }
    };

    if (businessId) {
      fetchLogo();
    }
  }, [businessId]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/client-user`,
        { withCredentials: true }
      );
      // Assuming notifications have a unique _id
      const newNotifications = response.data.data.notification || [];
      const unreadNotifications = newNotifications.filter((n) => !n.isRead).length;
      setNotifications(newNotifications);
      setUnreadCount(unreadNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const toggleNotifications = () => {
    // --- Close delete menu when closing notifications ---
    setOpenDeleteMenu(null); 
    setShowNotifications((prev) => !prev);
    if (!showNotifications) {
      fetchNotifications();
    }
  };
  
  const toggleProfileDropdown = () => {
    setShowProfileDropdown((prev) => !prev);
  };

  const handleLogout = async () => {
    try {
      // 1. Set a "logout flag" in session storage so App.js knows where to go
      sessionStorage.setItem('lastPortal', 'client');
  
      // 2. Perform the API call
      await axios.post(
        `${process.env.REACT_APP_API_URL}/client-user/logout`,
        {},
        { withCredentials: true }
      );
  
      // 3. Clear everything and Go to login
      dispatch(logout());
      sessionStorage.clear();
      localStorage.clear();
      
      // 4. Force navigation to the client login specifically
      navigate("/client-login", { replace: true });
  
    } catch (error) {
      console.error("Logout failed", error);
      navigate("/client-login", { replace: true });
      dispatch(logout());
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // --- Mark as read on click as well ---
      if (!notification.isRead) {
        markAsRead(notification._id, null); // Pass null for index if not needed
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/event/${notification?.eventId}`,
        { withCredentials: true }
      );

      if (response.status === 200) {
        setEventData(response.data.data.event);
        setShowAccountInfo(false);
        handleTabSwitch('portal');
        setShowNotifications(false); // Close notification tray
        setOpenDeleteMenu(null); // Close delete menu

        if (notification?.title === 'Booking Confirmation') {
          setInitialBusinessEventTab(0);
        } else if (notification?.title === 'New Images Added to Gallery') {
          setInitialBusinessEventTab(5);
        } else if (notification?.title === "Invoice Received") {
          setInitialBusinessEventTab(1);
        } else if (notification?.title === "Photostrip Design Feedback Received" || notification?.title === "Photostrip Template Received") {
          setInitialBusinessEventTab(4);
        } else if (notification?.title === "Questionnaire Received") {
          setInitialBusinessEventTab(3);
        }
      } else {
        console.error('Failed to fetch event data:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching event data:', error);
    }
  };

  // --- NEW: Mark as Read Function ---
  const markAsRead = async (notificationId, index) => {
    // Find the notification to check its read status
    const notification = notifications.find(n => n._id === notificationId);
    
    // If it's already read, do nothing
    if (notification && notification.isRead) {
      return;
    }

    // Optimistic UI update
    setNotifications(prevNotifications =>
      prevNotifications.map(n =>
        n._id === notificationId ? { ...n, isRead: true } : n
      )
    );
    setUnreadCount(prevCount => Math.max(0, prevCount - 1));

    // API call to backend
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/client-user/notification/read`,
        { notificationId }, // Assumes backend needs the ID
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
      // Optional: Revert optimistic update on error
      // fetchNotifications(); // Easiest way to revert
    }
  };

  // --- NEW: Delete Notification Function ---
  const handleDeleteNotification = async (notificationId, index) => {
    const notification = notifications[index];
    
    // Optimistic UI update
    setNotifications(prevNotifications =>
      prevNotifications.filter(n => n._id !== notificationId)
    );

    // If deleting an unread notification, decrease count
    if (notification && !notification.isRead) {
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    }
    
    setOpenDeleteMenu(null); // Close the menu

    // API call to backend
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/client-user/notification/delete`,
        { notificationId }, // Assumes backend needs the ID
        { withCredentials: true }
      );
    } catch (error) {
      console.error("Error deleting notification:", error);
      // Optional: Revert optimistic update on error
      // fetchNotifications(); // Easiest way to revert
    }
  };

  return (
    <div>
      {/* --- Renders the new CSS styles --- */}
      
      
      <header className="header" style={{ height: "50px", background: "#ffffff" }}>
        {/* ... existing code ... */}
        <div style={{ display: "flex", width: "214%", marginTop: "-11px" }}>
          {/* Only mount the image tag once the logo URL is successfully fetched */}
          {logo && (
            <img
              style={{ marginLeft: "1%", width: "5%", cursor: 'pointer' }}
              src={logo}
              alt="Company Logo"
              className="clientBoothLogo"
              onError={(e) => { e.target.style.display = 'none'; }}
              onClick={() => window.location.reload()}
            />
          )}
        </div>
        <div style={{ display: "flex", width: "17rem", alignItems: "center", position: "relative" }}>
          <div ref={notificationIconRef} className="notification" onClick={toggleNotifications} style={{ position: "relative", cursor: "pointer" }}>
            <IoNotifications style={{ fontSize: "25px", color: "rgb(29,16,83)" }} />
            {unreadCount > 0 && (
              <span style={{ position: "absolute", top: "-4px", right: "17px", backgroundColor: "red", padding: '0.5px', color: "white", width: "18px", height: "18px", borderRadius: "50%", fontSize: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {unreadCount}
              </span>
            )}
          </div>
          <div ref={profileIconRef} className="profile" onClick={toggleProfileDropdown}>
          <img 
                src={profileImage || defaultProfilePic} 
                alt="Profile" 
                style={{ marginBottom: "6px", width: "38px", height: "38px", borderRadius: "50%", objectFit: "cover" }} 
                onError={(e) => { e.target.src = defaultProfilePic; }}
              />
          </div>
        </div>
      </header>
      
      {showProfileDropdown && (
        <div ref={profileDropdownRef} className="notification-layover" style={{ width: "11%", right: "2%", padding: "0px", display: "block" }}>
          {/* ... existing code ... */}
          <button
            className="profile-dropdown-item"
            onClick={() => {
              handleTabSwitch('landing');
              setShowProfileDropdown(false);
            }}
          >
            <MdDashboard /> Dashboard
          </button>
          <button
            className="profile-dropdown-item"
            onClick={() => {
              onAccountDropdownClick();
              setShowProfileDropdown(false);
            }}
          >
            <FaRegUser /> Profile
          </button>
          <button className="profile-dropdown-item" onClick={handleLogout}>
            <MdOutlineLogout /> Log Out
          </button>
        </div>
      )}

      {showNotifications && (
        <div ref={notificationDropdownRef} className="notification-layover">
          <div className="notification-title">
            <h2>Notifications</h2>
            <hr />
          </div>
          {notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <div
                key={notification._id} // Use unique _id as key
                className={`notification-item ${notification.isRead ? "" : "unread"}`}
                // --- NEW: Mark as read on hover ---
                onMouseEnter={() => markAsRead(notification._id, index)}
                style={{ position: "relative" }} // Needed for dots positioning
              >
                <div onClick={() => handleNotificationClick(notification)} style={{cursor: "pointer"}}>
                  <h4>{notification.title}</h4>
                  <p>{notification.message}</p>
                  <span className="timestamp">{new Date(notification.timestamp).toLocaleString()}</span>
                </div>

                {/* --- UPDATED: Three dots menu --- */}
                <div 
                  className="notification-dots"
                  onClick={(e) => {
                    e.stopPropagation(); // Stop click from triggering 'handleNotificationClick'
                    
                    if (openDeleteMenu === index) {
                      setOpenDeleteMenu(null); // Close if clicking same
                    } else {
                      // Get position of the clicked dots icon
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMenuPosition({
                        // Position the menu's top-left corner
                        top: rect.bottom - 10, // 2px below the dot
                        left: rect.left, // Position it to the left (dropdown width is ~110px)
                      });
                      setOpenDeleteMenu(index); // Open menu for this item
                    }
                  }}
                >
                  <PiDotsThreeOutlineFill />
                </div>

                {/* --- REMOVED: Delete Dropdown is now outside --- */}
                
              </div>
            ))
          ) : (
            <p className="no-notifications">No new notifications</p>
          )}
        </div>
      )}

      {/* --- NEW: Single, Floating Delete Menu --- */}
      {/* This renders *outside* the notification list, so it can't be cut off */}
      {showNotifications && openDeleteMenu !== null && (
        <div
          ref={deleteMenuRef}
          className="delete-dropdown"
          // Position it using 'fixed' and the coordinates we saved in state
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
        >
          <button
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              // Get the correct notification using the stored index
              const notificationToDelete = notifications[openDeleteMenu];
              if (notificationToDelete) {
                handleDeleteNotification(notificationToDelete._id, openDeleteMenu);
              }
              setOpenDeleteMenu(null); // Close menu
            }}
          >
            <FaTrash /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientHeader;